import { useMemo } from 'react';
import type { Polygon, Position } from 'geojson';
import type { Layer } from '@deck.gl/core';
import { PolygonLayer } from '@deck.gl/layers';
import { useMapColor } from '../useMapColor';
import { useMapVisibility } from '../useMapVisibility';
import { useReferenceAreaData } from '../../data/useReferenceAreaData';
import { COLORS } from '../../../constants/color';
import { alpha, toRgb } from '../../../utils/color';

type UseReferenceAreaLayer = {
  referenceAreaLayer: Layer;
};

const useReferenceAreaLayer: () => UseReferenceAreaLayer = () => {
  const { mapColor } = useMapColor();
  const { visibleAreaType } = useMapVisibility();
  const { referenceArea } = useReferenceAreaData();

  const data = useMemo((): Polygon[] => {
    return referenceArea ? [referenceArea] : [];
  }, [referenceArea]);

  const referenceAreaLayer = new PolygonLayer<Polygon>({
    id: 'reference-area-layer',
    data: data,
    getPolygon: (polygon: Polygon): Position[] => {
      return polygon.coordinates[0];
    },
    getFillColor: toRgb(COLORS.transparent),
    getLineColor:
      mapColor === 'light'
        ? toRgb(alpha('#3C3C3C', 0.6))
        : toRgb(alpha('#F0F0F0', 0.8)),
    getLineWidth: 30,
    lineWidthUnits: 'meters',
    lineWidthMinPixels: 2,
    lineWidthMaxPixels: 5,
    lineJointRounded: true,
    autoHighlight: false,
    pickable: false,
    visible: !!visibleAreaType,
    updateTriggers: {
      getLineColor: [mapColor],
    },
  });

  return { referenceAreaLayer };
};

export { useReferenceAreaLayer };
